import {type ReactElement, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {AddItemGridButton} from './components/add-item-grid-button/add-item-grid-button';
import type {AddItemGridButtonProps} from './components/add-item-grid-button/add-item-grid-button.types';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {isMyDesignsHidden, isTemplatesPopupHidden, onAddBrandAssetClickedHandler, showTemplatesPopupPulsatingDot} from '@Components/poster-editor/library/poster-editor-library';
import {updatePanelProperties, updatePanelSizeData} from '@Components/panel/panel-reducer';
import {AddItemPanelLoading} from '@Panels/poster-add-items-panel/components/add-item-panel-loading';
import {AiMenuOpenSource, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {getNoOfGridItems} from '@Components/base-grid';
import {isUserPremium} from '@Libraries/user.library';
import {AI_IMAGE_UPSELL_PANEL_HEIGHT_PX} from '@Panels/ai-image-panel/ai-image-panel.types';
import {addGeneratedVoiceToDesign} from '@Libraries/text-to-speech.library';
import {AiSubtitleMenuOpenedFrom} from '@Libraries/ai-transcript.library';
import {getAiSubtitlePanelId, getAiVideoPanelId} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.library';
import {
  AI_OPTIONS_HEIGHT,
  AI_OPTIONS_WITH_BANNER_HEIGHT,
  AI_SUBTITLES_HEIGHT,
  AI_SUBTITLES_HEIGHT_PAYG,
} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.types';
import {TEXT_OPTIONS_HEIGHT} from '@Panels/poster-add-items-panel/components/text-options/text-options.type';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './poster-add-items-panel.module.scss';
import {getHasUserSeenAiVideo} from '@Libraries/ai-video.library';
import {AI_CAROUSEL_CONTENT_TYPES_ALL} from '@Panels/stock-panel/stock-panel.types';

export enum AddItemOptions {
  DEFAULT = 'default',
  TEMPLATE = 'template',
  MEDIA = 'media',
  UPLOADS = 'uploads',
  TEXT = 'text',
  SLIDESHOW = 'slideshow',
  LAYOUT = 'layout',
  AI_IMAGE = 'ai-image',
  AI_VIDEO = 'ai-video',
  AI_VOICE = 'ai-voice',
  AI_SUBTITLES = 'ai-subtitles',
  AI_MENU = 'AI_MENU',
  RECORD = 'record',
  DESIGN = 'design',
}

interface AddItemsPanelProps {
  panelId: string;
  panelToShowOnOpen?: AddItemOptions;
}

const DEFAULT_PANEL_CLOSE_THRESHOLD = 230;
const AI_IMAGE_GRID_ID = 'AI_IMAGE_GRID_BOTTOM_BAR';
const DEFAULT_PANEL_FALLBACK_HEIGHT = '638px';
const ADD_ITEMS_HEADER_CLASS = 'js-add-items-panel-header';

function PosterAddItemsPanel({panelToShowOnOpen = AddItemOptions.DEFAULT, ...props}: AddItemsPanelProps): ReactElement<any> {
  const [selectedPanel, setSelectedPanel] = useState(panelToShowOnOpen);
  const [panelModule, setPanelModule] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const onClose = useDefaultPanelClose(props.panelId);
  const dispatch = useAppDispatch();
  const numberOfAiImageGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, AI_IMAGE_GRID_ID);
  });
  const showAiVideoAsNewFeature = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const measuredDefaultHeightRef = useRef<number | null>(null);

  useEffect(() => {
    updateHeightInRedux(panelToShowOnOpen);
    updatePanelCloseThreshold(panelToShowOnOpen);
  }, []);

  useEffect((): void => {
    void setPanels();
  }, [selectedPanel]);

  useLayoutEffect((): void => {
    measureDefaultPanelHeight();
  }, [selectedPanel, loading]);

  const measureDefaultPanelHeight = (): void => {
    if (selectedPanel !== AddItemOptions.DEFAULT || !panelRef.current || !contentRef.current) {
      return;
    }
    const headerElement = panelRef.current.querySelector(`.${ADD_ITEMS_HEADER_CLASS}`);
    const headerHeight = headerElement instanceof HTMLElement ? headerElement.offsetHeight : 0;
    const totalHeight = headerHeight + contentRef.current.scrollHeight;
    if (totalHeight <= 0 || measuredDefaultHeightRef.current === totalHeight) {
      return;
    }
    measuredDefaultHeightRef.current = totalHeight;
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {height: `${totalHeight}px`},
      })
    );
  };

  const updatePanelCloseThreshold = (type: AddItemOptions): void => {
    dispatch(
      updatePanelProperties({
        panelId: props.panelId,
        bottomSheetCloseThreshold: type === AddItemOptions.DEFAULT ? DEFAULT_PANEL_CLOSE_THRESHOLD : undefined,
      })
    );
  };

  const updateHeightInRedux = (type: AddItemOptions): void => {
    updatePanelCloseThreshold(type);
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: getHeightAccordingToMorphType(type),
        },
      })
    );
  };

  const getHeightAccordingToMorphType = (type: AddItemOptions): string => {
    switch (type) {
      case AddItemOptions.AI_IMAGE:
        if (!isUserPremium()) {
          return `${AI_IMAGE_UPSELL_PANEL_HEIGHT_PX}px`;
        }
        return '1000px';
      case AddItemOptions.MEDIA:
      case AddItemOptions.AI_VIDEO:
      case AddItemOptions.AI_VOICE:
      case AddItemOptions.UPLOADS:
      case AddItemOptions.TEMPLATE:
      case AddItemOptions.DESIGN:
        return '1000px';
      case AddItemOptions.AI_SUBTITLES:
        return isUserPremium() ? AI_SUBTITLES_HEIGHT : AI_SUBTITLES_HEIGHT_PAYG;
      case AddItemOptions.TEXT:
        return TEXT_OPTIONS_HEIGHT;
      case AddItemOptions.RECORD:
      case AddItemOptions.SLIDESHOW:
        return '308px';
      case AddItemOptions.AI_MENU:
        return showAiVideoAsNewFeature.current ? AI_OPTIONS_WITH_BANNER_HEIGHT : AI_OPTIONS_HEIGHT;
      case AddItemOptions.LAYOUT:
        return '364px';
      case AddItemOptions.DEFAULT:
      default:
        return measuredDefaultHeightRef.current !== null ? `${measuredDefaultHeightRef.current}px` : DEFAULT_PANEL_FALLBACK_HEIGHT;
    }
  };

  const getHeader = (): ReactElement<any> => {
    return <PanelHeader className={ADD_ITEMS_HEADER_CLASS} title={window.i18next.t('pmwjs_add_capitalized')} onClose={onClose} />;
  };

  const openSubPanel = (option: AddItemOptions): void => {
    updateHeightInRedux(option);
    setSelectedPanel(option);
  };

  const getGridItems = (): AddItemGridButtonProps[] => {
    return [
      {
        id: 'bottombar-uploads-media',
        icon: 'icon-upload',
        label: window.i18next.t('pmwjs_uploads'),
        onClick: (): void => {
          openSubPanel(AddItemOptions.UPLOADS);
        },
      },
      ...(isTemplatesPopupHidden()
        ? []
        : [
            {
              id: 'bottombar-templates-option',
              icon: 'icon-multiple-layouts',
              label: window.i18next.t('pmwjs_templates'),
              labelClassName: 'capitalize-sentence',
              hasPulsatingDot: showTemplatesPopupPulsatingDot(),
              onClick: (): void => {
                trackPosterBuilderGA4Events(GA4EventName.TEMPLATES_MENU_OPEN);
                openSubPanel(AddItemOptions.TEMPLATE);
              },
            },
          ]),
      {
        id: 'bottombar-stock-media',
        icon: 'icon-media',
        label: window.i18next.t('pmwjs_media'),
        onClick: (): void => {
          openSubPanel(AddItemOptions.MEDIA);
        },
      },
      {
        id: 'bottombar-plain-text',
        icon: 'icon-text',
        label: window.i18next.t('pmwjs_text'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.TEXT_MENU_OPEN);
          openSubPanel(AddItemOptions.TEXT);
        },
      },
      ...(isMyDesignsHidden()
        ? []
        : [
            {
              id: 'bottombar-my-designs',
              icon: 'icon-designs',
              label: window.i18next.t('pmwjs_my_designs'),
              onClick: (): void => {
                trackPosterBuilderGA4Events(GA4EventName.DESIGNS_MENU_OPEN);
                openSubPanel(AddItemOptions.DESIGN);
              },
            },
          ]),
      {
        id: 'bottombar-ai-image',
        icon: 'icon-ai-image',
        label: window.i18next.t('pmwjs_ai_images'),
        isAi: true,
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.BOTTOM_DRAWER});
          openSubPanel(AddItemOptions.AI_IMAGE);
        },
      },
      {
        id: 'bottombar-ai-video',
        icon: 'icon-video-ai',
        label: window.i18next.t('pmwjs_ai_videos'),
        isAi: true,
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.AI_VIDEO_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.BOTTOM_DRAWER});
          openSubPanel(AddItemOptions.AI_VIDEO);
        },
      },
      {
        id: 'bottombar-ai-voice',
        icon: 'icon-ai-voice',
        label: window.i18next.t('pmwjs_ai_voice'),
        isAi: true,
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.BOTTOM_DRAWER});
          openSubPanel(AddItemOptions.AI_VOICE);
        },
      },
      {
        id: 'bottombar-ai-subtitles',
        icon: 'icon-ai-subtitles',
        label: window.i18next.t('pmwjs_ai_subtitles'),
        isAi: true,
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_MENU, {[GA4EventParamName.MENU_OPENED_FROM]: AiSubtitleMenuOpenedFrom.ADD_AI_OPTION});
          openSubPanel(AddItemOptions.AI_SUBTITLES);
        },
      },
      {
        id: 'bottombar-layout',
        icon: 'icon-divider',
        label: window.i18next.t('pmwjs_layout'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.LAYOUT_MENU_OPEN);
          openSubPanel(AddItemOptions.LAYOUT);
        },
      },
      {
        id: 'bottombar-qr-code',
        icon: 'icon-qr-content',
        label: window.i18next.t('pmwjs_qr_code'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.QR_CODE_ADDED);
          addQrCodeToPage();
          onClose();
        },
      },
      {
        id: 'record-options',
        icon: 'icon-radio-button-checked',
        label: window.i18next.t('pmwjs_record'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.RECORD_MENU_OPEN);
          openSubPanel(AddItemOptions.RECORD);
        },
      },
      {
        id: 'bottombar-draw',
        icon: 'icon-pencil',
        label: window.i18next.t('pmwjs_draw'),
        onClick: (): void => {
          window.posterEditor?.whiteboard?.drawing.toggleDrawMode();
          onClose();
        },
      },
      {
        id: 'bottombar-slideshow',
        icon: 'icon-slideshow',
        label: window.i18next.t('pmwjs_slideshow'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.SLIDESHOW_MENU_OPEN);
          openSubPanel(AddItemOptions.SLIDESHOW);
        },
      },
    ];
  };

  const getAiImagePanelHeightClass = (): string | undefined => {
    if (!isUserPremium()) {
      return undefined;
    }
    return numberOfAiImageGridItems > 0 ? styles.fullAIGridContent : styles.emptyAIGridContent;
  };

  const getGridButtons = (): ReactElement[] => {
    return getGridItems().map((item) => {
      return <AddItemGridButton key={item.id} {...item} />;
    });
  };

  const getContent = (): ReactElement<any> => {
    return (
      <PanelContent className={styles.container}>
        <div ref={contentRef} className={`spacing-p-l-4 spacing-p-r-4 spacing-p-t-1 spacing-p-b-4 ${styles.scrollArea}`}>
          <div className={styles.grid}>{getGridButtons()}</div>
        </div>
      </PanelContent>
    );
  };

  const setPanels = async (): Promise<void> => {
    let module: any;
    setLoading(true);
    switch (selectedPanel) {
      case AddItemOptions.TEXT:
        module = await import('./components/text-options/text-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            onDone={() => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              onClose();
            }}
          />
        );
        break;
      case AddItemOptions.TEMPLATE:
        module = await import('./components/templates-panel/templates-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.AI_IMAGE:
        module = await import('@Panels/ai-image-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            shouldLoadDataUrlImage
            onAddSelected={onAddItems}
            gridID={AI_IMAGE_GRID_ID}
            showInPanelUpsell
            className={getAiImagePanelHeightClass()}
          />
        );
        break;
      case AddItemOptions.AI_VIDEO:
        module = await import('@Panels/ai-video-panel/ai-video-panel');
        setPanelModule(
          <module.default
            panelId={getAiVideoPanelId(props.panelId)}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            onAddSelected={onAddItems}
          />
        );
        break;
      case AddItemOptions.AI_VOICE:
        module = await import('@Panels/ai-voice-panel/ai-voice-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            onAddAudioItem={async (itemId: string): Promise<void> => {
              onClose();
              await addGeneratedVoiceToDesign(Number(itemId));
            }}
          />
        );
        break;
      case AddItemOptions.AI_SUBTITLES:
        module = await import('@Panels/ai-subtitle-panel/ai-subtitle-panel');
        setPanelModule(
          <module.default
            isMobile
            panelId={getAiSubtitlePanelId(props.panelId)}
            onClose={onClose}
            onDone={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              onClose();
            }}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.AI_MENU:
        module = await import('./components/ai-options/ai-options');
        setPanelModule(
          <module.default
            onDone={() => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              onClose();
            }}
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              showAiVideoAsNewFeature.current = false;
            }}
            showNewAiFeaturePulse={showAiVideoAsNewFeature.current}
          />
        );
        break;
      case AddItemOptions.LAYOUT:
        module = await import('./components/layout-options/layout-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.SLIDESHOW:
        module = await import('./components/slide-show-options/slide-show-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.MEDIA:
        module = await import('@Panels/stock-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            defaultTab={UploadsMediaType.IMAGE}
            shouldLoadDataUrlImage
            aiCarouselContentTypes={AI_CAROUSEL_CONTENT_TYPES_ALL}
            onAddSelected={onAddItems}
            enableSelectionFooter={isMobile}
            selectionFooterCtaText={window.i18next.t('pmwjs_add_selected')}
            onAddBrandAsset={(): void => {
              void onAddBrandAssetClickedHandler();
            }}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.UPLOADS:
        module = await import('@Panels/user-media-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            defaultTab={UploadsMediaType.IMAGE}
            shouldLoadDataUrlImage
            onAddSelected={onAddItems}
            onAddBrandAsset={(): void => {
              void onAddBrandAssetClickedHandler();
            }}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.RECORD:
        module = await import('./components/record-options/record-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.DESIGN:
        module = await import('@Components/poster-editor/components/poster-add-item-bar/components/my-designs-popup/my-designs-popup');
        setPanelModule(
          <module.MyDesignsPopup
            showAsMobilePanel
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.DEFAULT:
      default:
        setPanelModule(<Panel ref={panelRef} panelHeader={getHeader()} panelContent={getContent()} />);
    }
    setLoading(false);
  };

  const initAiVideoNewStatus = async (): Promise<void> => {
    try {
      const response = await getHasUserSeenAiVideo();
      showAiVideoAsNewFeature.current = !response;
    } catch (e) {
      showAiVideoAsNewFeature.current = false;
    }
  };

  const init = (): void => {
    void initAiVideoNewStatus();
  };

  useEffect(() => {
    init();
  }, []);

  if (!panelModule || loading) {
    return <AddItemPanelLoading />;
  }

  return panelModule as ReactElement<any>;
}

export default PosterAddItemsPanel;
